import { GuestData } from '../types';

// API base URL from environment variable or fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const getGuestData = async (code: string): Promise<GuestData | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/guest/${code}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GuestData = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching guest data:', error);
    throw error;
  }
};

export const updateRSVP = async (
  code: string,
  status: 'yes' | 'no',
  confirmedSeats?: number,
  confirmedKidsSeats?: number
): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/rsvp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        status,
        confirmedSeats: status === 'yes' ? confirmedSeats : 0,
        confirmedKidsSeats: status === 'yes' ? confirmedKidsSeats : 0,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.success === true;
  } catch (error) {
    console.error('Error updating RSVP:', error);
    throw error;
  }
};

// Photo upload types
export interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  expiresIn: number;
}

export interface PhotoItem {
  key: string;
  url: string;
  lastModified: string;
  size: number;
}

export interface ListPhotosResponse {
  photos: PhotoItem[];
}

// Get presigned URL for uploading a photo
export const getPresignedUploadUrl = async (
  code: string,
  file: File
): Promise<PresignedUrlResponse> => {
  try {
    const params = new URLSearchParams({
      code,
      filename: file.name,
      contentType: file.type,
    });

    const response = await fetch(`${API_BASE_URL}/photos/upload-url?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    throw error;
  }
};

// Upload file directly to S3 using presigned URL (simple version without progress)
export const uploadFileToS3 = async (
  uploadUrl: string,
  file: File
): Promise<void> => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      if (response.status === 403 || response.status === 400) {
        throw new Error('El enlace de subida ha expirado o no es válido. Por favor intenta de nuevo.');
      }
      throw new Error(`Error uploading file: ${response.status}`);
    }
  } catch (error) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
};

// Progress callback type
export type ProgressCallback = (progress: number) => void;

// Upload file to S3 with progress tracking using XMLHttpRequest
export const uploadFileToS3WithProgress = (
  uploadUrl: string,
  file: File,
  onProgress?: ProgressCallback
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else if (xhr.status === 403 || xhr.status === 400) {
        reject(new Error('El enlace de subida ha expirado o no es válido. Por favor intenta de nuevo.'));
      } else {
        reject(new Error(`Error uploading file: ${xhr.status}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Error de red al subir el archivo. Verifica tu conexión.'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Subida cancelada.'));
    });

    xhr.addEventListener('timeout', () => {
      reject(new Error('La subida tardó demasiado. Por favor intenta de nuevo.'));
    });

    // Configure and send request
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    // Set a generous timeout for large files (2 hours)
    xhr.timeout = 2 * 60 * 60 * 1000;
    xhr.send(file);
  });
};

// Get list of photos for a guest
export const getGuestPhotos = async (code: string): Promise<PhotoItem[]> => {
  try {
    const params = new URLSearchParams({ code });

    const response = await fetch(`${API_BASE_URL}/photos/list?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ListPhotosResponse = await response.json();
    return data.photos;
  } catch (error) {
    console.error('Error fetching guest photos:', error);
    throw error;
  }
};

// Delete a guest media file
// filename should be just the filename part (without the code prefix)
export const deleteGuestMedia = async (code: string, filename: string): Promise<boolean> => {
  try {
    // Encode filename for URL
    const encodedFilename = encodeURIComponent(filename);
    const params = new URLSearchParams({ code });

    const response = await fetch(`${API_BASE_URL}/photos/${encodedFilename}?${params}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.success === true;
  } catch (error) {
    console.error('Error deleting media:', error);
    throw error;
  }
};